// This would be a serverless function for scraping
// For now, we'll create a client-side only solution

export class ClientSideScrapingService {
  // Security: Allowed domains for scraping
  private static readonly ALLOWED_DOMAINS = [
    'amazon.com', 'amazon.co.uk', 'amazon.ca', 'amazon.de', 'amazon.fr', 'amazon.in',
    'target.com', 'walmart.com', 'bestbuy.com', 'homedepot.com',
    'etsy.com', 'ebay.com', 'shopify.com', 'bigcommerce.com',
    'woocommerce.com', 'magento.com', 'noon.com'
  ];

  // Security: Blocked patterns for SSRF protection
  private static readonly BLOCKED_PATTERNS = [
    'localhost', '127.0.0.1', '192.168.', '10.', '172.16.', '172.17.',
    '172.18.', '172.19.', '172.20.', '172.21.', '172.22.', '172.23.',
    '172.24.', '172.25.', '172.26.', '172.27.', '172.28.', '172.29.',
    '172.30.', '172.31.', '0.0.0.0', '::1'
  ];

  /**
   * Validate URL for security before scraping
   */
  private static validateUrl(url: string): boolean {
    try {
      // Basic URL validation
      const urlObj = new URL(url);
      
      // Check protocol
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        throw new Error('Only HTTP and HTTPS protocols are allowed');
      }
      
      // Block internal/private IPs
      if (this.BLOCKED_PATTERNS.some(pattern => urlObj.hostname.includes(pattern))) {
        throw new Error('Internal URLs are not allowed for scraping');
      }
      
      // Only allow specific domains
      const hostname = urlObj.hostname.toLowerCase();
      if (!this.ALLOWED_DOMAINS.some(domain => hostname.includes(domain))) {
        throw new Error('Domain not allowed for scraping');
      }
      
      return true;
    } catch (error) {
      console.error('URL validation failed:', error);
      return false;
    }
  }
  /**
   * Clean URL by removing unnecessary query parameters that might trigger anti-bot protection
   */
  private static cleanUrl(url: string): string {
    try {
      const urlObj = new URL(url);
      
      // For Amazon URLs, remove all query parameters to avoid anti-bot detection
      if (urlObj.hostname.includes('amazon.')) {
        return `${urlObj.protocol}//${urlObj.hostname}${urlObj.pathname}`;
      }
      
      // For other URLs, remove tracking parameters but keep essential ones
      const essentialParams = ['id', 'product', 'item', 'sku'];
      const searchParams = new URLSearchParams();
      
      // Use Array.from to iterate over URLSearchParams
      Array.from(urlObj.searchParams.entries()).forEach(([key, value]) => {
        if (essentialParams.some(param => key.toLowerCase().includes(param))) {
          searchParams.set(key, value);
        }
      });
      
      const cleanUrl = `${urlObj.protocol}//${urlObj.hostname}${urlObj.pathname}`;
      return searchParams.toString() ? `${cleanUrl}?${searchParams.toString()}` : cleanUrl;
    } catch (error) {
      console.warn('Failed to clean URL:', error);
      return url;
    }
  }

  /**
   * Scrape product data using server-side API with retry logic
   */
  static async scrapeProduct(url: string): Promise<any> {
    console.log('🔍 Starting product scraping for:', url);
    
    // Validate URL for security first
    if (!this.validateUrl(url)) {
      throw new Error('URL validation failed - security check');
    }
    
    console.log('🔍 Attempting server-side scraping for:', url);
    
    // Clean the URL by removing unnecessary query parameters
    const cleanUrl = this.cleanUrl(url);
    console.log('🧹 Cleaned URL:', cleanUrl);
    
    // Try server-side scraping with retries
    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        console.log(`🔄 Attempt ${attempt}/3 for server-side scraping`);
        
        const response = await fetch('http://localhost:3001/api/scrape', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ url: cleanUrl }),
        });

        if (response.ok) {
          const data = await response.json();
          console.log('✅ Server-side scraping successful:', data);
          
          // Check if we got incomplete data (Amazon blocking)
          if (data.title === 'Amazon.in' && !data.price && !data.image) {
            console.warn(`⚠️ Attempt ${attempt}: Got incomplete data, likely blocked by Amazon`);
            if (attempt < 3) {
              console.log('🔄 Retrying in 2 seconds...');
              await new Promise(resolve => setTimeout(resolve, 2000));
              continue;
            } else {
              throw new Error('Incomplete data received after all retries');
            }
          }
          
          return data;
        } else {
          console.warn(`⚠️ Attempt ${attempt}: Server-side scraping failed:`, response.statusText);
          if (attempt < 3) {
            console.log('🔄 Retrying in 2 seconds...');
            await new Promise(resolve => setTimeout(resolve, 2000));
            continue;
          } else {
            throw new Error(`Server error: ${response.statusText}`);
          }
        }
      } catch (error) {
        console.warn(`⚠️ Attempt ${attempt}: Server error:`, error);
        if (attempt < 3) {
          console.log('🔄 Retrying in 2 seconds...');
          await new Promise(resolve => setTimeout(resolve, 2000));
          continue;
        } else {
          console.warn('⚠️ All server attempts failed, falling back to client-side methods');
        }
      }
    }

    // Fallback to client-side methods if server is not available
    return this.fallbackScraping(url);
  }

  /**
   * Fallback client-side scraping methods
   */
  private static async fallbackScraping(url: string): Promise<any> {
    console.log('🔄 Starting fallback scraping for:', url);
    
    // For Etsy and other blocked sites, use URL parsing as primary method
    if (url.includes('etsy.com')) {
      console.log('🎨 Detected Etsy URL, using URL parsing method');
      return this.parseEtsyURL(url);
    }
    
    try {
      // Method 1: Try to fetch with CORS headers
      const response = await fetch(url, {
        mode: 'cors',
        headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
          'Accept-Language': 'en-US,en;q=0.5',
          'Accept-Encoding': 'gzip, deflate',
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
        }
      });

      if (response.ok) {
        const html = await response.text();
        return this.parseHTML(html, url);
      }
    } catch (error) {
      console.warn('Direct fetch failed:', error);
    }

        // Method 2: Use a public CORS proxy
        try {
          const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`;
          const response = await fetch(proxyUrl);
          
          if (response.ok) {
            const data = await response.json();
            return this.parseHTML(data.contents, url);
          }
        } catch (error) {
          console.warn('AllOrigins proxy failed:', error);
        }

        // Method 3: Try alternative CORS proxy
        try {
          const proxyUrl = `https://cors-anywhere.herokuapp.com/${url}`;
          const response = await fetch(proxyUrl, {
            headers: {
              'X-Requested-With': 'XMLHttpRequest'
            }
          });
          
          if (response.ok) {
            const html = await response.text();
            return this.parseHTML(html, url);
          }
        } catch (error) {
          console.warn('CORS-anywhere proxy failed:', error);
        }

        // Method 4: Try another CORS proxy
        try {
          const proxyUrl = `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(url)}`;
          const response = await fetch(proxyUrl);
          
          if (response.ok) {
            const html = await response.text();
            return this.parseHTML(html, url);
          }
        } catch (error) {
          console.warn('CodeTabs proxy failed:', error);
        }

        // Method 5: Try alternative CORS proxy - CORS Proxy
        try {
          const proxyUrl = `https://corsproxy.io/?${encodeURIComponent(url)}`;
          const response = await fetch(proxyUrl, {
            headers: {
              'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
              'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36'
            }
          });
          
          if (response.ok) {
            const html = await response.text();
            return this.parseHTML(html, url);
          }
        } catch (error) {
          console.warn('CORS Proxy failed:', error);
        }

        // Method 6: Try another CORS proxy - ThingProxy
        try {
          const proxyUrl = `https://thingproxy.freeboard.io/fetch/${encodeURIComponent(url)}`;
          const response = await fetch(proxyUrl);
          
          if (response.ok) {
            const html = await response.text();
            return this.parseHTML(html, url);
          }
        } catch (error) {
          console.warn('ThingProxy failed:', error);
        }

        // Method 7: Try ProxyCORS
        try {
          const proxyUrl = `https://proxycors.com/api/v1?url=${encodeURIComponent(url)}`;
          const response = await fetch(proxyUrl);
          
          if (response.ok) {
            const html = await response.text();
            return this.parseHTML(html, url);
          }
        } catch (error) {
          console.warn('ProxyCORS failed:', error);
        }

    // Method 6: Fallback to URL analysis
    console.log('🔄 All scraping methods failed, falling back to URL analysis');
    const result = this.parseFromURL(url);
    console.log('📊 URL analysis result:', result);
    return result;
  }

  /**
   * Parse YQL results
   */
  private static parseYQLResults(results: any, url: string): any {
    console.log('🔍 Parsing YQL results:', results);
    
    const title = results.title || results.meta?.find((m: any) => m.property === 'og:title')?.content || 'Unknown Product';
    const image = results.meta?.find((m: any) => m.property === 'og:image')?.content;
    const price = results.meta?.find((m: any) => m.property === 'product:price:amount')?.content;
    
    return {
      title: this.formatTitle(title),
      description: `Product from ${new URL(url).hostname}`,
      price: this.extractPrice(price),
      image,
      currency: this.detectCurrencyFromUrl(url),
      original_url: url
    };
  }

  /**
   * Parse HTML content
   */
  private static parseHTML(html: string, url: string): any {
    // Parsing HTML content
    
    // Create a virtual DOM parser
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Extract meta tags - prioritize product-specific selectors
    const ogTitle = doc.querySelector('meta[property="og:title"]')?.getAttribute('content');
    const metaTitle = doc.querySelector('meta[name="title"]')?.getAttribute('content');
    const productTitle = doc.querySelector('#productTitle')?.textContent?.trim();
    const h1Title = doc.querySelector('h1[data-automation-id="product-title"]')?.textContent?.trim();
    const h1Large = doc.querySelector('h1.a-size-large')?.textContent?.trim();
    const h1General = doc.querySelector('h1')?.textContent?.trim();
    const pageTitle = doc.title;
    
    // Debug logging removed for cleaner console
    
    const title = ogTitle || metaTitle || productTitle || h1Title || h1Large || h1General || pageTitle;

    const description = doc.querySelector('meta[property="og:description"]')?.getAttribute('content') ||
                        doc.querySelector('meta[name="description"]')?.getAttribute('content');

        const image = doc.querySelector('meta[property="og:image"]')?.getAttribute('content') ||
                      doc.querySelector('meta[name="image"]')?.getAttribute('content') ||
                      doc.querySelector('img[alt*="product"]')?.getAttribute('src') ||
                      doc.querySelector('.product-image img')?.getAttribute('src') ||
                      doc.querySelector('#landingImage')?.getAttribute('src') ||
                      doc.querySelector('#imgBlkFront')?.getAttribute('src') ||
                      doc.querySelector('.a-dynamic-image')?.getAttribute('src') ||
                      doc.querySelector('[data-testid="product-image"]')?.getAttribute('src') ||
                      doc.querySelector('.productImage')?.getAttribute('src') ||
                      doc.querySelector('.main-image')?.getAttribute('src') ||
                      doc.querySelector('[class*="ProductImage"]')?.getAttribute('src') ||
                      doc.querySelector('[data-qa="product-image"]')?.getAttribute('src') ||
                      doc.querySelector('.image-container img')?.getAttribute('src') ||
                      this.extractAmazonImage(url, doc);

        // Enhanced price extraction - Noon.com specific selectors
        const priceText = doc.querySelector('meta[property="product:price:amount"]')?.getAttribute('content') ||
                          doc.querySelector('meta[name="price"]')?.getAttribute('content') ||
                          doc.querySelector('.price')?.textContent?.trim() ||
                          doc.querySelector('[class*="price"]')?.textContent?.trim() ||
                          doc.querySelector('[data-testid*="price"]')?.textContent?.trim() ||
                          doc.querySelector('[data-testid="product-price"]')?.textContent?.trim() ||
                          doc.querySelector('.priceNow')?.textContent?.trim() ||
                          doc.querySelector('.price-now')?.textContent?.trim() ||
                          doc.querySelector('[class*="Price"]')?.textContent?.trim() ||
                          doc.querySelector('.amount')?.textContent?.trim() ||
                          doc.querySelector('[data-qa="product-price"]')?.textContent?.trim();

    const price = this.extractPrice(priceText);

    // Enhanced currency extraction
    const currencyText = doc.querySelector('meta[property="product:price:currency"]')?.getAttribute('content') ||
                         doc.querySelector('meta[name="currency"]')?.getAttribute('content') ||
                         doc.querySelector('[data-currency]')?.getAttribute('data-currency') ||
                         priceText?.match(/[A-Z]{3}/)?.[0];

    // Try to extract from data layers (Google Tag Manager, etc.)
    const dataLayerCurrency = this.extractFromDataLayer(doc, 'currency') || 
                             this.extractFromDataLayer(doc, 'priceCurrency');

    const currency = currencyText || dataLayerCurrency || this.detectCurrencyFromUrl(url) || 'USD';

    const brand = doc.querySelector('meta[property="product:brand"]')?.getAttribute('content') ||
                  doc.querySelector('meta[name="brand"]')?.getAttribute('content') ||
                  doc.querySelector('[data-brand]')?.getAttribute('data-brand');

    console.log('📊 Extracted data:', {
      title,
      description,
      price,
      currency,
      image,
      brand,
      priceText,
      currencyText
    });

    return {
      title: title || 'Unknown Product',
      description,
      price,
      currency,
      image,
      brand,
      original_url: url
    };
  }

  /**
   * Extract Amazon product image using multiple methods
   */
  private static extractAmazonImage(url: string, doc: Document): string | undefined {
    console.log('🖼️ Extracting Amazon image for URL:', url);
    
    // Method 1: Try to extract product ID and construct image URL
    const productIdMatch = url.match(/\/dp\/([A-Z0-9]+)/);
    if (productIdMatch) {
      const productId = productIdMatch[1];
      console.log('🆔 Found product ID:', productId);
      
      // Try multiple Amazon image URL patterns - use simpler format first
      const imageUrls = [
        `https://m.media-amazon.com/images/I/${productId}.jpg`,
        `https://m.media-amazon.com/images/I/${productId}._AC_SL1500_.jpg`,
        `https://m.media-amazon.com/images/I/${productId}._AC_SX679_.jpg`,
        `https://images-na.ssl-images-amazon.com/images/I/${productId}.jpg`,
        `https://images-na.ssl-images-amazon.com/images/I/${productId}._AC_SL1500_.jpg`,
        `https://images.amazon.com/images/I/${productId}.jpg`
      ];
      
      console.log('🖼️ Generated image URLs:', imageUrls);
      // Return the first pattern (simplest format)
      return imageUrls[0];
    }
    
    // Method 2: Look for Amazon-specific image selectors
    const amazonSelectors = [
      '#landingImage',
      '#imgBlkFront', 
      '.a-dynamic-image',
      '#main-image',
      '.a-button-text img',
      '.a-carousel-image img'
    ];
    
    console.log('🔍 Searching for Amazon image selectors...');
    for (const selector of amazonSelectors) {
      const img = doc.querySelector(selector);
      if (img) {
        const src = img.getAttribute('src') || img.getAttribute('data-src');
        console.log(`✅ Found image with selector ${selector}:`, src);
        if (src && src.includes('amazon')) {
          return src;
        }
      }
    }
    
    console.log('❌ No Amazon image found');
    return undefined;
  }

  /**
   * Extract data from data layers and structured data
   */
  private static extractFromDataLayer(doc: Document, key: string): string | undefined {
    try {
      // Look for dataLayer in script tags
      const scripts = Array.from(doc.querySelectorAll('script'));
      for (const script of scripts) {
        const content = script.textContent || '';
        if (content.includes('dataLayer') || content.includes('gtag')) {
          // Try to extract dataLayer content
          const dataLayerMatch = content.match(/dataLayer\s*=\s*(\[.*?\])/s);
          if (dataLayerMatch) {
            try {
              // Clean up the dataLayer string before parsing
              let dataLayerString = dataLayerMatch[1];
              // Remove any trailing commas and fix common JSON issues
              dataLayerString = dataLayerString.replace(/,(\s*[}\]])/g, '$1');
              const dataLayer = JSON.parse(dataLayerString);
              for (const item of dataLayer) {
                if (item[key]) {
                  console.log(`📊 Found ${key} in dataLayer:`, item[key]);
                  return item[key];
                }
              }
            } catch (e) {
              console.warn('Failed to parse dataLayer, trying alternative extraction:', (e as Error).message);
              // Try to extract specific values using regex as fallback
              const currencyMatch = content.match(/currency['":\s]*['"]([A-Z]{3})['"]/i);
              if (key === 'currency' && currencyMatch) {
                console.log(`📊 Found ${key} via regex:`, currencyMatch[1]);
                return currencyMatch[1];
              }
            }
          }
        }
      }

      // Look for JSON-LD structured data
      const jsonLdScripts = Array.from(doc.querySelectorAll('script[type="application/ld+json"]'));
      for (const script of jsonLdScripts) {
        try {
          const data = JSON.parse(script.textContent || '');
          if (data[key]) {
            console.log(`📊 Found ${key} in JSON-LD:`, data[key]);
            return data[key];
          }
        } catch (e) {
          console.warn('Failed to parse JSON-LD:', e);
        }
      }
    } catch (error) {
      console.warn('Error extracting from data layer:', error);
    }
    
    return undefined;
  }

  /**
   * Detect currency from URL/domain
   */
  private static detectCurrencyFromUrl(url: string): string {
    const urlLower = url.toLowerCase();
    
    // UAE/Arab Emirates
    if (urlLower.includes('uae.') || urlLower.includes('.ae/') || urlLower.includes('microless') || urlLower.includes('amazon.ae') || urlLower.includes('noon.com')) {
      return 'AED';
    }
    
    // UK
    if (urlLower.includes('.co.uk/') || urlLower.includes('.uk/')) {
      return 'GBP';
    }
    
    // Canada
    if (urlLower.includes('.ca/')) {
      return 'CAD';
    }
    
    // Australia
    if (urlLower.includes('.com.au/') || urlLower.includes('.au/')) {
      return 'AUD';
    }
    
    // Europe
    if (urlLower.includes('.de/') || urlLower.includes('.fr/') || urlLower.includes('.it/') || 
        urlLower.includes('.es/') || urlLower.includes('.nl/')) {
      return 'EUR';
    }
    
    // Japan
    if (urlLower.includes('.jp/')) {
      return 'JPY';
    }
    
    // India
    if (urlLower.includes('.in/')) {
      return 'INR';
    }
    
    // Singapore
    if (urlLower.includes('.sg/')) {
      return 'SGD';
    }
    
    // Default to USD
    return 'USD';
  }

  /**
   * Extract price from text
   */
  private static extractPrice(priceText?: string): number | undefined {
    if (!priceText) return undefined;

    // Try multiple patterns for price extraction
    const patterns = [
      /[\d,]+\.?\d*/,  // Basic number pattern
      /(\d{1,3}(?:,\d{3})*(?:\.\d{2})?)/,  // Formatted number with commas
      /(\d+\.\d{2})/,  // Decimal format
      /(\d+)/  // Just digits
    ];

    for (const pattern of patterns) {
      const match = priceText.match(pattern);
      if (match) {
        const price = parseFloat(match[1] || match[0].replace(/,/g, ''));
        if (!isNaN(price) && price > 0) {
          return price;
        }
      }
    }

    return undefined;
  }

  /**
   * Parse URL for basic product information
   */
  private static parseFromURL(url: string): any {
    const urlObj = new URL(url);
    const currency = this.detectCurrencyFromUrl(url);
    
    if (urlObj.hostname.includes('amazon.')) {
      console.log('🛒 Processing Amazon URL:', url);
      const amazonData = this.parseAmazonURL(url);
      console.log('🛒 Amazon data:', amazonData);
      return { ...amazonData, currency };
    }
    
    if (urlObj.hostname.includes('target.com')) {
      return { ...this.parseTargetURL(url), currency };
    }
    
    if (urlObj.hostname.includes('noon.com')) {
      return { ...this.parseNoonURL(url), currency };
    }

    // Generic fallback
    const pathSegments = urlObj.pathname.split('/').filter(Boolean);
    const title = pathSegments[pathSegments.length - 1]
      ?.replace(/[-_]/g, ' ')
      ?.replace(/\.(html|htm|php)$/, '') || 'Product';

    return {
      title: this.formatTitle(title),
      price: undefined,
      image: undefined,
      currency
    };
  }

  private static parseAmazonURL(url: string): any {
    console.log('🔍 Parsing Amazon URL:', url);
    
    // Extract product ID from Amazon URL
    const dpMatch = url.match(/\/dp\/([A-Z0-9]+)/);
    const gpMatch = url.match(/\/gp\/product\/([A-Z0-9]+)/);
    const productId = dpMatch?.[1] || gpMatch?.[1];
    
    console.log('🆔 Product ID:', productId);
    
    // Extract title from URL path - improved regex for better parsing
    // Look for the pattern: /dp/PRODUCT_ID/TITLE_PART
    const pathMatch = url.match(/\/dp\/[A-Z0-9]+\/([^/?&]+)/);
    let title = 'Amazon Product';
    
    console.log('📝 Path match:', pathMatch);
    
    if (pathMatch && pathMatch[1]) {
      title = pathMatch[1]
        .replace(/[-_]/g, ' ')
        .replace(/\+/g, ' ')
        .split('?')[0]
        .split('&')[0]
        .split('/')[0] // Remove additional path segments
        .replace(/ref=sr.*$/i, '') // Remove ref=sr parameters
        .trim();
      
      console.log('📝 Extracted title:', title);
    }

    // Try to construct image URL for Amazon products
    let imageUrl = undefined;
    if (productId) {
      // Try different Amazon image URL patterns
      const imagePatterns = [
        `https://m.media-amazon.com/images/I/${productId}.jpg`,
        `https://m.media-amazon.com/images/I/${productId}._AC_SL1500_.jpg`,
        `https://m.media-amazon.com/images/I/${productId}._AC_SX679_.jpg`,
        `https://images-na.ssl-images-amazon.com/images/I/${productId}.jpg`,
        `https://images-na.ssl-images-amazon.com/images/I/${productId}._AC_SL1500_.jpg`
      ];
      
      // Use the first pattern (most likely to work)
      imageUrl = imagePatterns[0];
      console.log('🖼️ Generated image URL:', imageUrl);
      console.log('🖼️ Alternative patterns:', imagePatterns);
    }

    // Extract additional info from URL if available
    const description = `Product from Amazon.ae - ${title}`;

    const result = {
      title: this.formatTitle(title),
      description,
      price: undefined,
      image: imageUrl,
      currency: this.detectCurrencyFromUrl(url)
    };
    
    console.log('🎯 Final Amazon result:', result);
    return result;
  }

  private static parseTargetURL(url: string): any {
    const match = url.match(/\/p\/([^/]+)/);
    if (match) {
      return {
        title: this.formatTitle(match[1].replace(/[-_]/g, ' ')),
        price: undefined,
        image: undefined
      };
    }
    return { title: 'Target Product' };
  }

  private static parseNoonURL(url: string): any {
    console.log('🛒 Processing Noon URL:', url);
    
    // Extract product ID from Noon URL
    const productIdMatch = url.match(/\/N(\d+)V\//);
    const productId = productIdMatch?.[1];
    
    console.log('🆔 Noon Product ID:', productId);
    
    // Extract title from URL path
    const pathMatch = url.match(/\/uae-en\/([^/]+)\/N\d+V/);
    let title = 'Noon Product';
    
    console.log('📝 Noon path match:', pathMatch);
    
    if (pathMatch) {
      title = pathMatch[1]
        .replace(/[-_]/g, ' ')
        .replace(/\+/g, ' ')
        .split('?')[0]
        .split('&')[0]
        .trim();
      
      console.log('📝 Extracted Noon title:', title);
    }

    // For Noon.com, we can't easily extract price/image from URL alone
    // This would require scraping the actual page content
    const result = {
      title: this.formatTitle(title),
      description: `Product from Noon.com - ${title}`,
      price: undefined, // Would need scraping to get actual price
      image: undefined, // Would need scraping to get actual image
      currency: this.detectCurrencyFromUrl(url)
    };
    
    console.log('🎯 Final Noon result:', result);
    return result;
  }

  /**
   * Parse Etsy URL for basic product information
   */
  private static parseEtsyURL(url: string): any {
    try {
      const urlObj = new URL(url);
      
      // Extract listing ID from Etsy URL
      const listingMatch = url.match(/\/listing\/(\d+)/);
      const listingId = listingMatch?.[1];
      
      // Extract title from URL path
      const pathSegments = urlObj.pathname.split('/').filter(Boolean);
      let title = 'Etsy Product';
      
      // Look for descriptive text in the URL
      if (pathSegments.length > 2) {
        const titlePart = pathSegments[pathSegments.length - 1];
        if (titlePart && !titlePart.match(/^\d+$/)) {
          title = titlePart
            .replace(/[-_]/g, ' ')
            .replace(/\+/g, ' ')
            .split('?')[0]
            .split('&')[0]
            .trim();
        }
      }
      
      // Etsy image URLs have a complex structure that we can't reliably construct
      // The actual format is: https://i.etsystatic.com/{shopId}/r/il/{hash}/{imageId}/il_{dimensions}_{imageId}_{params}.jpg
      // Since we can't predict the shop ID, hash, or image ID, we'll return null
      // and let the placeholder image component handle the display
      let potentialImageUrl = null;
      
      // For Etsy, we can't easily get price/image without scraping
      // But we can provide a good title and description
      const description = `Handmade item from Etsy${listingId ? ` (Listing #${listingId})` : ''}. Image and price not available due to Etsy's complex URL structure and anti-scraping measures.`;
      
      return {
        title: this.formatTitle(title),
        description,
        price: null, // Would need scraping to get actual price
        image: potentialImageUrl, // Try to construct image URL (may not work)
        currency: 'USD', // Etsy typically uses USD
        brand: 'Etsy',
        availability: 'Unknown',
        original_url: url,
        note: 'Price and image may need manual entry due to Etsy blocking'
      };
    } catch (error) {
      console.warn('Failed to parse Etsy URL:', error);
      return {
        title: 'Etsy Product',
        description: 'Handmade item from Etsy. Price and image not available due to Etsy\'s anti-scraping measures.',
        price: null,
        image: null,
        currency: 'USD',
        brand: 'Etsy',
        availability: 'Unknown',
        original_url: url,
        note: 'Price and image may need manual entry due to Etsy blocking'
      };
    }
  }

  private static formatTitle(title: string): string {
    return title
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ')
      .substring(0, 100);
  }
}
